import React, { useState, useCallback, useEffect } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { Link } from 'react-router-dom'
import { saveAs } from 'file-saver'

import Container from '../Container/Container'
import LoadingOverlay from 'shared/genericComponents/LoadingOverlay/LoadingOverlay'
import { getIconButton } from 'shared/genericComponents/IconButton/IconButton'
import { useQueryParams, getInstanceType, round } from '../../shared/lib/utils'
import { usePaymentLink, useStripe } from '../../shared/lib/hooks'

import CheckoutForm from '../../shared/CheckoutForm/CheckoutForm'
import PaymentSummaryTable from 'shared/PaymentSummaryTable/PaymentSummaryTable'

import currencyFormatter from 'shared/utils/currencyFormatter'
import {
  createPaymentLinkPaymentIntent,
  updateStripePaymentLinkPaymentIntent,
} from '../../shared/lib/payments'

import * as styles from './PaymentLink.m.css'

const IconButton = getIconButton(styles.viewInvoiceIcon)

const PaymentLink: React.FC = (): React.ReactElement => {
  const params = useQueryParams<PaymentLinkQueryParams>()
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('ach')
  const [stripeLoading, setStripeLoading] = useState(false)
  const [intentInfo, setIntentInfo] = useState<IntentInfo>({})
  const [intentError, setIntentError] = useState<string | null>(null)

  const stripe = useStripe()

  const {
    data: paymentLinkData,
    isLoading: isPaymentLinkLoading,
    error: paymentLinkError,
  } = usePaymentLink(params.c)

  const invoice = paymentLinkData?.invoice ?? null
  const invoiceAmount = invoice?.remainingBalance ?? 0
  const ccFee = invoice?.ccFee ?? 0
  const customer = paymentLinkData?.customer ?? null

  let actualFee
  if (customer?.waiveCCFee || paymentMethod !== 'card') {
    actualFee = 0
  } else {
    actualFee = ccFee
  }

  const paymentAmount = invoiceAmount + actualFee

  useEffect(() => {
    const getIntent = async () => {
      setStripeLoading(true)
      console.log('Creating payment intent')
      const selectedInvoices = [invoice]

      try {
        const newIntentInfo = await createPaymentLinkPaymentIntent(
          params.c,
          paymentAmount,
          invoice.remainingBalance,
          actualFee,
          selectedInvoices.map((i) => i.invoiceId).join(', '),
          getInstanceType(),
          paymentMethod,
        )

        setIntentInfo(newIntentInfo)
      } catch (error) {
        // Don't retry; surface the error and stop loading
        setIntentError((error as Error).message)
        setStripeLoading(false)
      }
    }

    if (invoice && !intentInfo?.clientSecret && !intentError) {
      getIntent()
      // setStripeLoading(false)
    }
  }, [
    actualFee,
    intentInfo,
    intentError,
    invoice,
    params.c,
    paymentAmount,
    paymentMethod,
  ])

  const onViewInvoiceClicked = async () => {
    const doc = await fetch(
      `/api/paymentlinks/${params.c}/invoices/${invoice.invoiceId}`,
    )

    const str = await doc.text()
    const typedStr = `data:application/pdf;base64,${str}`

    const response = await fetch(typedStr)
    const blob = await response.blob()

    saveAs(blob, `${invoice.invoiceId}.pdf`)
  }

  const onStripePaymentMethodChanged = useCallback(
    (type: string) => {
      let fee, total
      switch (type) {
        case 'card': {
          setPaymentMethod('card')
          fee = round(ccFee, 2)
          total = round(invoiceAmount + fee, 2)
          break
        }
        default: {
          fee = 0
          total = round(invoiceAmount, 2)
          setPaymentMethod('ach')
        }
      }

      // There shouldn't be a way to get here without having created a payment
      // intent already.
      updateStripePaymentLinkPaymentIntent(
        params.c,
        intentInfo.paymentIntentId,
        total,
        invoice.remainingBalance,
        fee,
        invoice.invoiceId,
        getInstanceType(),
        paymentMethod,
      )
    },
    [
      invoiceAmount,
      ccFee,
      intentInfo?.paymentIntentId,
      invoice?.remainingBalance,
      invoice?.invoiceId,
      params.c,
      paymentMethod,
    ],
  )

  const onStripeElementsReady = useCallback(() => {
    setStripeLoading(false)
  }, [])

  return (
    <>
      {stripeLoading || isPaymentLinkLoading ? (
        <LoadingOverlay>Loading...</LoadingOverlay>
      ) : null}
      <Container>
        {paymentLinkError ? (
          <div>
            <h1>Invalid Payment Link.</h1>
            <p>Your payment link has expired or is otherwise invalid.</p>
          </div>
        ) : null}
        {!isPaymentLinkLoading && paymentLinkData ? (
          <div>
            <div className={styles.buttonContainer}>
              <IconButton
                onClick={onViewInvoiceClicked}
                icon="description"
                text="View Invoice"
              />
            </div>
            {!customer.waiveCCFee ? (
              <table>
                <thead>
                  <tr>
                    <th colSpan={3}>Surcharges vary by payment method</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Card</th>
                    <td>3%</td>
                    <td>{currencyFormatter.format(ccFee)}</td>
                  </tr>
                  <tr>
                    <th>ACH Bank Transfer</th>
                    <td>Free!</td>
                    <td>$0.00</td>
                  </tr>
                </tbody>
              </table>
            ) : null}
            <PaymentSummaryTable
              invoices={[invoice]}
              portalFeeAmount={actualFee}
              showSubtotals={!customer.waiveCCFee}
            />
          </div>
        ) : null}
        {intentError ? (
          <div>
            <h1>Unable to process payment.</h1>
            <p>{intentError} Please contact us to complete your payment.</p>
          </div>
        ) : null}
        {paymentAmount > 0 && intentInfo?.clientSecret ? (
          <div>
            <Elements
              stripe={stripe.data}
              options={{ clientSecret: intentInfo.clientSecret }}
            >
              <CheckoutForm
                stripe={stripe.data}
                amount={paymentAmount}
                name={customer?.name}
                email={customer?.billingEmail}
                onReady={onStripeElementsReady}
                paymentMethodChangeCallback={onStripePaymentMethodChanged}
              />
            </Elements>
          </div>
        ) : null}
        <div>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
      </Container>
    </>
  )
}

export default PaymentLink
